import axios from "axios";
import { Logger } from "../logger/logger";
import { useAuthStore } from "presentation/stores/use-auth-store";
import { authRepository } from "infra/repositories/auth-repository";
import { useOfflineStore } from "presentation/stores/use-offline-store";

const baseURL = process.env.REACT_APP_BASE_URL;
const logger = Logger.getInstance();
const DEFAULT_TIMEOUT = 60000;

export const httpClient = axios.create({
  baseURL,
  timeout: DEFAULT_TIMEOUT,
});

httpClient.interceptors.request.use(
  (config) => {
    // Configura timeout específico por URL para não afetar outras requisições
    const longTimeUrl = [
      "/tripsheet/setting",
      "/timesheet/setting",
      "/timesheetDay/bulk",
      "/distance/populate",
      "/project",
      "/vacationPlan"
    ];
    
    if (longTimeUrl.some((url) => config.url?.includes(url))) {
      config.timeout = 0; // Sem timeout para rotas demoradas
    } else {
      config.timeout = DEFAULT_TIMEOUT;
    }

    // Adiciona o Token de Autorização do Store do Zustand
    const { accessToken } = useAuthStore.getState();
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }

    // Adiciona o email do utilizador para auditoria, se disponível
    const { userEmployee } = useAuthStore.getState();
    if (userEmployee?.emailAddress) {
      config.headers["X-User-Email"] = userEmployee.emailAddress;
    }

    // Adiciona Header de Impersonation se o modo debug estiver ativo
    try {
      const { useImpersonationStore } = require("presentation/stores/use-impersonation-store");
      const { isDebugMode, impersonatedEmployeeId } = useImpersonationStore.getState();
      if (isDebugMode && impersonatedEmployeeId) {
        config.headers["X-Impersonate-User-Id"] = impersonatedEmployeeId;
      }
    } catch (e) {
      // Ignora
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Função para remover dados sensíveis dos logs de forma segura contra referências circulares
const sanitize = (data: any, seen = new WeakSet()): any => {
  if (!data || typeof data !== "object") return data;

  // Evita recursão em datas, regex, blobs, etc.
  if (data instanceof Date || data instanceof RegExp || typeof Blob !== "undefined" && data instanceof Blob) {
    return data.toString();
  }

  // Previne loop infinito / referências circulares (evita RangeError: Maximum call stack size exceeded)
  if (seen.has(data)) {
    return "[CIRCULAR]";
  }
  seen.add(data);

  const sensitiveFields = [
    "password",
    "token",
    "accessToken",
    "refreshToken",
    "authCode",
    "codeVerifier",
    "client_id",
    "client_secret",
  ];

  if (Array.isArray(data)) {
    return data.map((item) => sanitize(item, seen));
  }

  // Se for AxiosHeaders ou objeto com toJSON, converte para objeto plano antes de iterar
  const plainObject = typeof data.toJSON === "function" ? data.toJSON() : { ...data };
  const sanitized: Record<string, any> = {};

  Object.keys(plainObject).forEach((key) => {
    if (sensitiveFields.some((field) => key.toLowerCase().includes(field.toLowerCase()))) {
      sanitized[key] = "[REDACTED]";
    } else if (typeof plainObject[key] === "object" && plainObject[key] !== null) {
      sanitized[key] = sanitize(plainObject[key], seen);
    } else {
      sanitized[key] = plainObject[key];
    }
  });

  return sanitized;
};
  


//MOBILE & DESKTOP: Intercepta respostas e erros
httpClient.interceptors.response.use(
  (response) => {
    //MOBILE: Repões o estado online assim que uma requisição responde com sucesso
    if (useOfflineStore.getState().isOffline){
      useOfflineStore.getState().setOffline(false);
    }
    return response;
  },
  async (error) => {
    //MOBILE: Intercepta falhas de rede/sem internet
    const isNetworkFailure = !error.response || error.code === 'ERR_NETWORK' || !navigator.onLine;
    if (isNetworkFailure) {
      useOfflineStore.getState().setOffline(true);
    }
      
    const originalRequest = error.config;

    // Se o erro for 401 e não for uma tentativa de refresh, tenta renovar o token
    if (
      error.response?.status === 401 && 
      !originalRequest._retry && 
      !originalRequest.url?.includes("/auth/")
    ) {
      originalRequest._retry = true;
      const { refreshToken, setAuth, clearAuth } = useAuthStore.getState();

      if (refreshToken) {
        try {
          const response = await authRepository.refresh(refreshToken);
          const newTokenData = response.data;

          //Suportatanto tanto o formato antigo (string) quanto o novo (objeto com accessToken)
          const accessToken = newTokenData?.accessToken || newTokenData?.token || (typeof newTokenData === "string" ? newTokenData : null);

          if (accessToken) {
            setAuth(typeof newTokenData === "object" ?  newTokenData : { accessToken });
            
            // Atualiza o header da requisição original e repete
            originalRequest.headers.Authorization = `Bearer ${accessToken}`;
            return httpClient(originalRequest);
          }
        } catch (refreshError) {
          logger.error("Falha ao renovar token automaticamente", { refreshError: sanitize(refreshError) });
          clearAuth();
          // Redireciona para o login se necessário (ou deixa o componente tratar)
          return Promise.reject(refreshError);
        }
      } else {
        clearAuth();
      }
    }

    // Trata erros de resposta para logging
    if (error.config) {
      let payload;
      if (error.config.data) {
        try {
          payload = JSON.parse(error.config.data);
        } catch (e) {
          payload = error.config.data;
        }
      }

      logger.error(`Erro na requisição: ${error.message}`, {
        payload: sanitize(payload),
        url: error.config.url,
        method: error.config.method,
        headers: sanitize(error.config.headers),
        status: error.response?.status,
        responseData: sanitize(error.response?.data),
      });
    } else {
      logger.error(error.message, { error });
    }

    return Promise.reject(error);
  }
);

export const get = (url: string, params?: any) =>
  httpClient.get(url, { params });
export const post = (url: string, data: any) => httpClient.post(url, data);
export const put = (url: string, data: any) => httpClient.put(url, data);
export const del = (url: string) => httpClient.delete(url);
